/**
 * The single door to the backend. Everything goes through here so cookies,
 * error shape and JSON handling live in one place. Errors surface as ApiError
 * with a Polish message the UI can show directly.
 */

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Same-origin in prod (Caddy), proxied in dev (Vite) — cookie rides along.
  const init: RequestInit = { method, credentials: 'same-origin' }
  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json' }
    init.body = JSON.stringify(body)
  }

  let res: Response
  try {
    res = await fetch(path, init)
  } catch {
    // fetch only rejects on network failure — offline, server down.
    throw new ApiError(0, 'Brak połączenia z serwerem')
  }

  if (res.status === 204) return undefined as T

  let data: unknown = null
  const text = await res.text()
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error
        : null) ?? `Błąd serwera (${res.status})`

    // Session expired mid-work. Don't leave the user staring at a red box on a
    // page that will never load — put them back at the login screen. `/api/me`
    // is exempt: it 401s by design on first load when nobody is logged in yet.
    if (res.status === 401 && !path.endsWith('/api/me')) {
      onUnauthorized?.()
    }

    throw new ApiError(res.status, message)
  }

  return data as T
}

let onUnauthorized: (() => void) | undefined

/** Lets AuthProvider decide what "logged out" means, without api.ts importing React. */
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
}

/**
 * Create a record, or queue it if there's no connection (SPEC section 2 — new
 * records only, no edits). Online it POSTs normally; offline (or if the POST
 * dies on the network) it drops the draft into the outbox with an idempotency
 * key and returns `{ queued: true, localId }`. The caller shows "saved, will
 * send when back online" instead of navigating to a record the server hasn't
 * got yet.
 */
export async function createOrQueue<T>(
  kind: 'client' | 'note' | 'quote',
  path: string,
  body: Record<string, unknown>,
): Promise<{ queued: false; data: T } | { queued: true; localId?: string }> {
  const { enqueue } = await import('./offline/outbox.ts')
  if (navigator.onLine) {
    try {
      const data = await request<T>('POST', path, body)
      return { queued: false, data }
    } catch (e) {
      // A real server error (4xx/5xx) should surface, not get silently queued.
      // Only a network failure (status 0) means "no connection" — queue that.
      if (!(e instanceof ApiError) || e.status !== 0) throw e
    }
  }
  const { localEntityId } = await enqueue(kind, path, body)
  return { queued: true, ...(localEntityId ? { localId: localEntityId } : {}) }
}

export interface User {
  id: string
  code: string
  firstName: string
  lastName: string
  role: 'wlasciciel' | 'handlowiec' | 'serwis'
}
